import { type Building, type BuildingType } from '../constants';
import type { CoreGameState } from '../GameEngine';
import { findBuildingByCharacter, findBuildingCenter } from '../GameState';

type Position = CoreGameState['playerPos'];

export interface SoundSettings {
  readonly enabled: boolean;
  readonly masterVolume: number;
  readonly effectsVolume: number;
  readonly maxChannelsPerType: number;
  readonly maxDistance: number;
  readonly volumeFalloffStart: number;
}

export const DEFAULT_SOUND_SETTINGS: SoundSettings = {
  enabled: true,
  masterVolume: 0.75,
  effectsVolume: 1,
  maxChannelsPerType: 3,
  maxDistance: 18,
  volumeFalloffStart: 1,
};

interface BuildingSoundCandidate {
  readonly key: string;
  readonly buildingType: BuildingType;
  readonly distance: number;
  readonly soundPath: string;
}

interface ChannelController {
  ensurePlaying(): void;
  setVolume(volume: number): void;
  stop(): void;
}

interface ActiveChannel {
  readonly key: string;
  readonly buildingType: BuildingType;
  readonly controller: ChannelController;
  soundPath: string;
  lastDistance: number;
  volumeMultiplier: number;
}

const SOUND_VOLUME_PERCENTAGES: Record<string, number> = {
  '/audio/sfx/belt_hum.mp3': 80,
};

const SEAMLESS_LOOP_PATHS = new Set<string>(['/audio/sfx/belt_hum.mp3']);

const decibelToGain = (decibels: number): number => {
  return Math.pow(10, decibels / 20);
};

const MAX_DECIBEL_REDUCTION = 60;

const percentageToGain = (percentage: number): number => {
  const clampedPercentage = clamp(percentage, 0, 100);
  if (clampedPercentage <= 0) {
    return 0;
  }

  const reduction = 100 - clampedPercentage;
  const decibels = -(MAX_DECIBEL_REDUCTION * reduction) / 100;
  return decibelToGain(decibels);
};

const DISTANCE_FALLOFF_EXPONENT = 10;

const clamp = (value: number, min: number, max: number): number => {
  if (value < min) {
    return min;
  }
  if (value > max) {
    return max;
  }
  return value;
};

const isFiniteNumber = (value: number): value is number => {
  return Number.isFinite(value);
};

const normalizeSoundSettingsValue = (
  value: number,
  fallback: number
): number => {
  return isFiniteNumber(value) ? value : fallback;
};

export const normalizeSoundSettings = (
  settings: SoundSettings
): SoundSettings => {
  const normalizedMaxDistance = clamp(
    normalizeSoundSettingsValue(
      settings.maxDistance,
      DEFAULT_SOUND_SETTINGS.maxDistance
    ),
    1,
    128
  );

  const normalizedMaxChannels = Math.round(
    clamp(
      normalizeSoundSettingsValue(
        settings.maxChannelsPerType,
        DEFAULT_SOUND_SETTINGS.maxChannelsPerType
      ),
      1,
      12
    )
  );

  const normalizedFalloff = clamp(
    normalizeSoundSettingsValue(
      settings.volumeFalloffStart,
      DEFAULT_SOUND_SETTINGS.volumeFalloffStart
    ),
    0,
    normalizedMaxDistance
  );

  const normalizedMasterVolume = clamp(
    normalizeSoundSettingsValue(
      settings.masterVolume,
      DEFAULT_SOUND_SETTINGS.masterVolume
    ),
    0,
    1
  );

  const normalizedEffectsVolume = clamp(
    normalizeSoundSettingsValue(
      settings.effectsVolume,
      DEFAULT_SOUND_SETTINGS.effectsVolume
    ),
    0,
    1
  );

  return {
    enabled: Boolean(settings.enabled),
    masterVolume: normalizedMasterVolume,
    effectsVolume: normalizedEffectsVolume,
    maxChannelsPerType: normalizedMaxChannels,
    maxDistance: normalizedMaxDistance,
    volumeFalloffStart: normalizedFalloff,
  };
};

export class SoundEngine {
  private readonly channels: Map<BuildingType, ActiveChannel[]> = new Map();
  private readonly isSupported: boolean;
  private readonly bufferCache: Map<string, Promise<AudioBuffer>> = new Map();
  private settings: SoundSettings;
  private audioContext?: AudioContext;
  private isSuspended: boolean = false;
  private waitingForInteraction: boolean = false;
  private readonly interactionUnlockHandler = (): void => {
    const context = this.audioContext;
    if (!context) {
      this.clearInteractionUnlock();
      return;
    }

    this.tryResumeAudioContext(context);
  };

  constructor(settings: SoundSettings = DEFAULT_SOUND_SETTINGS) {
    this.isSupported = SoundEngine.isAudioSupported();
    this.settings = normalizeSoundSettings(settings);
  }

  static isAudioSupported(): boolean {
    return typeof window !== 'undefined' && typeof Audio !== 'undefined';
  }

  reset(): void {
    if (!this.isSupported) {
      return;
    }

    this.clearInteractionUnlock();

    this.channels.forEach(channelList => {
      channelList.forEach(channel => this.stopChannel(channel));
    });
    this.channels.clear();
  }

  update(state: CoreGameState): void {
    if (!this.isSupported) {
      return;
    }

    if (this.isSuspended) {
      return;
    }

    if (!this.settings.enabled) {
      if (this.channels.size > 0) {
        this.reset();
      }
      return;
    }

    const candidates = this.collectSoundCandidates(state);
    const desiredChannelsByType = this.selectChannelsByType(candidates);

    const activeTypes = new Set<BuildingType>(desiredChannelsByType.keys());

    // Stop channels for building types that should be silent
    this.channels.forEach((existingChannels, buildingType) => {
      if (!activeTypes.has(buildingType)) {
        existingChannels.forEach(channel => this.stopChannel(channel));
        this.channels.delete(buildingType);
      }
    });

    desiredChannelsByType.forEach((desiredChannels, buildingType) => {
      const existingChannels = this.channels.get(buildingType) ?? [];
      const keepChannels: ActiveChannel[] = [];
      const desiredKeys = new Set(desiredChannels.map(channel => channel.key));

      // Stop any channels that are no longer needed
      existingChannels.forEach(channel => {
        if (!desiredKeys.has(channel.key)) {
          this.stopChannel(channel);
        }
      });

      desiredChannels.forEach(candidate => {
        let channel = existingChannels.find(
          existing => existing.key === candidate.key
        );

        if (channel && channel.soundPath !== candidate.soundPath) {
          this.stopChannel(channel);
          channel = undefined;
        }

        if (!channel) {
          channel = this.createChannel(candidate);
        }

        if (!channel) {
          return;
        }

        channel.volumeMultiplier = this.getVolumeMultiplier(
          candidate.soundPath
        );

        this.updateChannelVolume(channel, candidate.distance);
        keepChannels.push(channel);
      });

      this.channels.set(buildingType, keepChannels);
    });
  }

  setSuspended(suspended: boolean): void {
    if (!this.isSupported || this.isSuspended === suspended) {
      this.isSuspended = suspended;
      return;
    }

    this.isSuspended = suspended;

    if (suspended) {
      this.clearInteractionUnlock();
      this.channels.forEach(channelList => {
        channelList.forEach(channel => this.stopChannel(channel));
      });
      this.channels.clear();

      if (this.audioContext && this.audioContext.state === 'running') {
        void this.audioContext.suspend().catch(() => {
          // Ignore suspension failures; browsers may require user interaction first.
        });
      }
      return;
    }

    if (this.audioContext) {
      this.tryResumeAudioContext(this.audioContext);
    }
  }

  private collectSoundCandidates(
    state: CoreGameState
  ): BuildingSoundCandidate[] {
    const { playerPos, buildingLayer } = state;
    const uniqueCenters = new Set<string>();
    const candidates: BuildingSoundCandidate[] = [];

    Object.entries(buildingLayer).forEach(([posKey, char]) => {
      const building = findBuildingByCharacter(char);
      if (!building || !building.workingSound) {
        return;
      }

      const center = this.resolveBuildingCenter(
        posKey,
        building,
        buildingLayer
      );
      if (!center) {
        return;
      }

      const centerKey = `${center.x},${center.y}`;
      if (uniqueCenters.has(centerKey)) {
        return;
      }

      uniqueCenters.add(centerKey);

      const distance = this.calculateDistance(playerPos, center);
      if (distance > this.settings.maxDistance) {
        return;
      }

      candidates.push({
        key: centerKey,
        buildingType: building.id,
        distance,
        soundPath: building.workingSound,
      });
    });

    return candidates;
  }

  private selectChannelsByType(
    candidates: BuildingSoundCandidate[]
  ): Map<BuildingType, BuildingSoundCandidate[]> {
    const byType = new Map<BuildingType, BuildingSoundCandidate[]>();

    candidates.forEach(candidate => {
      const list = byType.get(candidate.buildingType) ?? [];
      list.push(candidate);
      byType.set(candidate.buildingType, list);
    });

    byType.forEach((list, buildingType) => {
      list.sort((a, b) => a.distance - b.distance);
      if (list.length > this.settings.maxChannelsPerType) {
        byType.set(
          buildingType,
          list.slice(0, this.settings.maxChannelsPerType)
        );
      }
    });

    return byType;
  }

  private resolveBuildingCenter(
    posKey: string,
    building: Building,
    buildingLayer: CoreGameState['buildingLayer']
  ): Position | null {
    const [xStr, yStr] = posKey.split(',');
    const x = Number.parseInt(xStr ?? '', 10);
    const y = Number.parseInt(yStr ?? '', 10);

    if (!Number.isFinite(x) || !Number.isFinite(y)) {
      return null;
    }

    if (
      building.size &&
      (building.size.width > 1 || building.size.height > 1)
    ) {
      const center = findBuildingCenter(x, y, building, buildingLayer);
      if (center) {
        return center;
      }
    }

    return { x, y };
  }

  private createChannel(
    candidate: BuildingSoundCandidate
  ): ActiveChannel | undefined {
    const controller = this.createChannelController(candidate.soundPath);
    if (!controller) {
      return undefined;
    }

    const volumeMultiplier = this.getVolumeMultiplier(candidate.soundPath);
    const initialVolume =
      this.calculateVolume(candidate.distance) * volumeMultiplier;

    controller.setVolume(initialVolume);
    controller.ensurePlaying();

    return {
      key: candidate.key,
      buildingType: candidate.buildingType,
      controller,
      soundPath: candidate.soundPath,
      lastDistance: candidate.distance,
      volumeMultiplier,
    };
  }

  private updateChannelVolume(channel: ActiveChannel, distance: number): void {
    channel.controller.ensurePlaying();
    const targetVolume =
      this.calculateVolume(distance) * channel.volumeMultiplier;
    channel.controller.setVolume(targetVolume);
    channel.lastDistance = distance;
  }

  private stopChannel(channel: ActiveChannel): void {
    channel.controller.stop();
  }

  private createChannelController(
    soundPath: string
  ): ChannelController | undefined {
    if (this.shouldUseSeamlessLoop(soundPath)) {
      const controller = this.createSeamlessLoopController(soundPath);
      if (controller) {
        return controller;
      }
    }
    return this.createHtmlAudioController(soundPath);
  }

  private shouldUseSeamlessLoop(soundPath: string): boolean {
    return SEAMLESS_LOOP_PATHS.has(soundPath);
  }

  private createHtmlAudioController(
    soundPath: string
  ): ChannelController | undefined {
    try {
      const audio = new Audio(soundPath);
      audio.loop = true;
      audio.preload = 'auto';
      return new HtmlAudioController(audio);
    } catch {
      return undefined;
    }
  }

  private createSeamlessLoopController(
    soundPath: string
  ): ChannelController | undefined {
    const context = this.getAudioContext();
    if (!context) {
      return undefined;
    }

    this.tryResumeAudioContext(context);

    const bufferPromise = this.loadAudioBuffer(context, soundPath);
    return new SeamlessLoopController(context, bufferPromise, error => {
      this.handleContextResumeError(error);
    });
  }

  private getAudioContext(): AudioContext | undefined {
    if (typeof window === 'undefined') {
      return undefined;
    }

    if (this.audioContext) {
      return this.audioContext;
    }

    const AudioContextCtor =
      window.AudioContext ??
      (
        window as unknown as {
          webkitAudioContext?: typeof AudioContext;
        }
      ).webkitAudioContext;

    if (!AudioContextCtor) {
      return undefined;
    }

    this.audioContext = new AudioContextCtor();
    this.tryResumeAudioContext(this.audioContext);
    return this.audioContext;
  }

  private loadAudioBuffer(
    context: AudioContext,
    soundPath: string
  ): Promise<AudioBuffer> {
    const existing = this.bufferCache.get(soundPath);
    if (existing) {
      return existing;
    }

    const bufferPromise = fetch(soundPath)
      .then(response => {
        if (!response.ok) {
          throw new Error(`Failed to load audio asset: ${soundPath}`);
        }
        return response.arrayBuffer();
      })
      .then(
        arrayBuffer =>
          new Promise<AudioBuffer>((resolve, reject) => {
            context.decodeAudioData(arrayBuffer, resolve, reject);
          })
      );

    this.bufferCache.set(soundPath, bufferPromise);
    return bufferPromise;
  }

  private getVolumeMultiplier(soundPath: string): number {
    const percentage = SOUND_VOLUME_PERCENTAGES[soundPath];
    if (typeof percentage !== 'number') {
      return 1;
    }
    return percentageToGain(percentage);
  }

  private calculateDistance(a: Position, b: Position): number {
    const dx = a.x - b.x;
    const dy = a.y - b.y;
    const squared = dx * dx + dy * dy;
    if (!isFiniteNumber(squared)) {
      return this.settings.maxDistance;
    }
    return Math.sqrt(squared);
  }

  private calculateVolume(distance: number): number {
    const baseVolume = this.settings.masterVolume * this.settings.effectsVolume;

    if (distance <= this.settings.volumeFalloffStart) {
      return baseVolume;
    }

    const distanceBeyondStart = distance - this.settings.volumeFalloffStart;
    const remainingDistance =
      this.settings.maxDistance - this.settings.volumeFalloffStart;
    if (remainingDistance <= 0) {
      return 0;
    }

    const normalized = clamp(1 - distanceBeyondStart / remainingDistance, 0, 1);
    const curved = Math.pow(normalized, DISTANCE_FALLOFF_EXPONENT);
    return curved * baseVolume;
  }

  updateSettings(settings: SoundSettings): void {
    this.settings = normalizeSoundSettings(settings);

    if (!this.settings.enabled) {
      this.reset();
      return;
    }

    this.channels.forEach(channelList => {
      channelList.forEach(channel => {
        const volume =
          this.calculateVolume(channel.lastDistance) * channel.volumeMultiplier;
        channel.controller.setVolume(volume);
      });
    });
  }

  private tryResumeAudioContext(context: AudioContext): void {
    if (context.state !== 'suspended') {
      this.clearInteractionUnlock();
      return;
    }

    void context
      .resume()
      .then(() => {
        this.clearInteractionUnlock();
      })
      .catch(error => {
        this.handleContextResumeError(error);
      });
  }

  private handleContextResumeError(error: unknown): void {
    const errorName = (error as { name?: string } | undefined)?.name;
    if (errorName === 'NotAllowedError') {
      this.requestInteractionUnlock();
    }
  }

  private requestInteractionUnlock(): void {
    if (!this.isSupported || typeof window === 'undefined') {
      return;
    }

    if (this.waitingForInteraction) {
      return;
    }

    this.waitingForInteraction = true;
    window.addEventListener('pointerdown', this.interactionUnlockHandler);
    window.addEventListener('keydown', this.interactionUnlockHandler);
    window.addEventListener('touchstart', this.interactionUnlockHandler);
  }

  private clearInteractionUnlock(): void {
    if (!this.waitingForInteraction || typeof window === 'undefined') {
      this.waitingForInteraction = false;
      return;
    }

    window.removeEventListener('pointerdown', this.interactionUnlockHandler);
    window.removeEventListener('keydown', this.interactionUnlockHandler);
    window.removeEventListener('touchstart', this.interactionUnlockHandler);
    this.waitingForInteraction = false;
  }
}

export default SoundEngine;

class HtmlAudioController implements ChannelController {
  private readonly audio: HTMLAudioElement;

  constructor(audio: HTMLAudioElement) {
    this.audio = audio;
  }

  ensurePlaying(): void {
    if (this.audio.paused) {
      void this.audio.play().catch(() => {
        // Autoplay restrictions may prevent immediate playback. The browser
        // will start playback after user interaction.
      });
    }
  }

  setVolume(volume: number): void {
    this.audio.volume = clamp(volume, 0, 1);
  }

  stop(): void {
    this.audio.pause();
    this.audio.currentTime = 0;
  }
}

class SeamlessLoopController implements ChannelController {
  private readonly context: AudioContext;
  private readonly gain: GainNode;
  private readonly bufferPromise: Promise<AudioBuffer>;
  private source: AudioBufferSourceNode | null = null;
  private desiredVolume = 0;
  private stopped = false;
  private readonly onResumeDenied: ((error: unknown) => void) | undefined;

  constructor(
    context: AudioContext,
    bufferPromise: Promise<AudioBuffer>,
    onResumeDenied?: (error: unknown) => void
  ) {
    this.context = context;
    this.bufferPromise = bufferPromise;
    this.onResumeDenied = onResumeDenied;
    this.gain = context.createGain();
    this.gain.gain.value = 0;
    this.gain.connect(context.destination);

    this.bufferPromise
      .then(buffer => {
        if (this.stopped) {
          return;
        }
        this.start(buffer);
        this.applyVolume();
      })
      .catch(() => {
        this.stop();
      });
  }

  ensurePlaying(): void {
    if (this.stopped) {
      return;
    }

    if (this.context.state === 'suspended') {
      void this.context.resume().catch(error => {
        this.onResumeDenied?.(error);
      });
    }
  }

  setVolume(volume: number): void {
    this.desiredVolume = clamp(volume, 0, 1);
    this.applyVolume();
  }

  stop(): void {
    if (this.stopped) {
      return;
    }

    this.stopped = true;
    try {
      this.source?.stop();
    } catch {
      // Ignore failures from stopping an already stopped source.
    }
    this.source?.disconnect();
    this.gain.disconnect();
  }

  private start(buffer: AudioBuffer): void {
    if (this.stopped) {
      return;
    }

    const source = this.context.createBufferSource();
    source.buffer = buffer;
    source.loop = true;
    source.connect(this.gain);
    try {
      source.start(0);
    } catch {
      // Ignore start failures; some browsers throw if the buffer is empty.
    }
    this.source = source;
  }

  private applyVolume(): void {
    this.gain.gain.value = this.desiredVolume;
  }
}
